import {
  IBodyMetricModel,
  ITargetInfo,
  ITargetMetricValues,
  IWeightTargetInfo,
  MetricToImperialConverter,
  ScanProgress,
  UserMetricInfo,
  UserTargetTypes,
  WeightTargetStep,
  ProgramType,
} from 'fitbeat.models';
import { Platform } from 'react-native';
import { UserProfile } from 'fitbeat.gym-core';
import moment from 'moment-timezone';
import { UserServiceClient } from 'fitbeat.api-clients';
import { initialiseBodyScan, setIsScanContinuingFromWeightTarget, setScanProgress } from '../BodyScale/actions';
import { selectIsScanContinuingFromWeightTarget } from '../BodyScale/selectors';
import { IAppStore } from '../HomeWorkouts';
import { requestBodyMetricStatsSuccess } from '../Tracking/actions';
import { toggleBodyScanModal, toggleWeightTargetModal } from '../ui/redux';
import {
  getTargetInfo, weightTargetInfoFromGoalSelector, weightTargetStepSelector, isChangingTargetSelector,
} from './selectors';

import {
  INITIAL_TARGET_SET_DONE,
  LOAD_CURRENT_TARGET_INFO,
  LOADED_CURRENT_TARGET_INFO,
  REQUEST_WEIGHT_TARGET_CALC, REQUEST_WEIGHT_TARGET_CALC_SUCCESS, REQUEST_WEIGHT_TARGET_INFO,
  REQUEST_WEIGHT_TARGET_INFO_SUCCESS, REQUEST_WEIGHT_TARGET_RESET, REQUEST_WEIGHT_TARGET_RESET_SUCCESS,
  REQUEST_WEIGHT_TARGET_SAVE, REQUEST_WEIGHT_TARGET_SAVE_FAIL, REQUEST_WEIGHT_TARGET_SAVE_SUCCESS,
  SET_HAS_SCALE, SET_PAL_VALUE, SET_TARGET_WEIGHT, SET_WEIGHT_TARGET_STEP,
  UPDATE_MILESTONE_REQUEST, UPDATE_MILESTONE_REQUEST_SUCCESS, REQUEST_CONTINUE_TO_SET_TARGET_SUCCESS,
  REQUEST_CONTINUE_TO_SET_TARGET, REQUEST_CONTINUE_TO_SET_TARGET_FAILURE, LOAD_CHANGE_TARGET_SCREEN_REQUEST,
  LOAD_CHANGE_TARGET_SCREEN_SUCCESS, LOAD_CHANGE_TARGET_SCREEN_FAILURE, CLOSE_TARGET_ADJUST_SCREEN,
  REMOVE_TARGET_SUCCESS, REMOVE_TARGET_REQUEST, REMOVE_TARGET_FAILURE,
  SHOW_CONTINUE_ON_APP_SCREEN,
} from './actionTypes';
import { toggleRemoveTargetModal } from '../ui';
import { closeAllModals } from '../ui/redux/uiActions';

export const showContinueOnAppScreen = (show: boolean) => ({
  type: SHOW_CONTINUE_ON_APP_SCREEN,
  payload: show,
});

export const requestWeightTargetInfo = (reviewedWeight, hideLoader = false) => ({
  type: REQUEST_WEIGHT_TARGET_INFO,
  payload: {
    reviewedWeight,
    hideLoader,
  },
});

export const requestWeightTargetInfoSuccess = (result, userTargetType) => ({
  type: REQUEST_WEIGHT_TARGET_INFO_SUCCESS,
  payload: {
    ...result,
    userTargetType,
  },
});

export const resetTargetRequest = () => ({
  type: REQUEST_WEIGHT_TARGET_RESET,
});

export const resetTargetRequestSuccess = (result) => ({
  type: REQUEST_WEIGHT_TARGET_RESET_SUCCESS,
  payload: {
    ...result,
  },
});

export const continueToSetTargetRequest = () => ({
  type: REQUEST_CONTINUE_TO_SET_TARGET,
});

export const continueToSetTargetSuccess = () => ({
  type: REQUEST_CONTINUE_TO_SET_TARGET_SUCCESS,
});

export const continueToSetTargetFailure = () => ({
  type: REQUEST_CONTINUE_TO_SET_TARGET_FAILURE,
});

const updateMilestoneRequest = () => ({
  type: UPDATE_MILESTONE_REQUEST,
});

const updateMilestoneRequestSuccess = () => ({
  type: UPDATE_MILESTONE_REQUEST_SUCCESS,
});

const requestWeightTargetCalc = () => ({
  type: REQUEST_WEIGHT_TARGET_CALC,
});

const requestWeightTargetCalcSuccess = (weightTarget, userTargetType, minMaxDefault = undefined) => ({
  type: REQUEST_WEIGHT_TARGET_CALC_SUCCESS,
  payload: {
    ...weightTarget,
    minMaxDefault,
    userTargetType,
  },
});

const requestWeightAndTargetSave = () => ({
  type: REQUEST_WEIGHT_TARGET_SAVE,
});

const requestWeightAndTargetSaveSuccess = () => ({
  type: REQUEST_WEIGHT_TARGET_SAVE_SUCCESS,
});

const requestWeightAndTargetSaveFailure = (error) => ({
  type: REQUEST_WEIGHT_TARGET_SAVE_FAIL,
  payload: error,
});

const loadCurrentTargetInfo = () => ({
  type: LOAD_CURRENT_TARGET_INFO,
});

const loadChangeTargetScreenRequest = () => ({
  type: LOAD_CHANGE_TARGET_SCREEN_REQUEST,
});

const loadChangeTargetScreenSuccess = (target: ITargetInfo, minMaxDefault: any, bodyScaleScanStats: any) => ({
  type: LOAD_CHANGE_TARGET_SCREEN_SUCCESS,
  payload: {
    bodyScaleScanStats,
    minMaxDefault,
    target,
  },
});

const loadChangeTargetScreenFailure = (error) => ({
  type: LOAD_CHANGE_TARGET_SCREEN_FAILURE,
  payload: error,
});

const loadedCurrentTargetInfo = (minMaxDefault, userTargetType) => ({
  type: LOADED_CURRENT_TARGET_INFO,
  payload: {
    minMaxDefault,
    userTargetType,
  },
});

const removeTargetRequest = () => ({
  type: REMOVE_TARGET_REQUEST,
});

const removeTargetSuccess = (bodyMetricSet: IBodyMetricModel, target: IWeightTargetInfo) => ({
  type: REMOVE_TARGET_SUCCESS,
  payload: {
    bodyMetricSet,
    target,
  },
});

const removeTargetFailure = () => ({
  type: REMOVE_TARGET_FAILURE,
});

export const setWeightTargetStep = (step) => ({
  type: SET_WEIGHT_TARGET_STEP,
  payload: {
    step,
  },
});

export const setTargetWeight = (targetWeight, calculating = false) => ({
  type: SET_TARGET_WEIGHT,
  payload: {
    targetWeight,
    calculating,
  },
});

export const setPALValue = (PALValue: number) => ({
  type: SET_PAL_VALUE,
  payload: {
    PALValue,
  },
});

export const initialTargetSetDone = () => ({
  type: INITIAL_TARGET_SET_DONE,
});

export const setHasScale = (hasScale: boolean | null) => ({
  type: SET_HAS_SCALE,
  payload: {
    hasScale,
  },
});

export const closeTargetAdjust = () => ({
  type: CLOSE_TARGET_ADJUST_SCREEN,
});

export const closeTargetAdjustScreen = () => async (dispatch, getState: () => IAppStore) => {
  dispatch(toggleWeightTargetModal(false));
  dispatch(closeTargetAdjust());
};

export const initialiseWeightTargetSet = (userProfile) => async (dispatch, getState: () => IAppStore) => {
  const hasScale = UserProfile.hasSmartScale(userProfile);
  dispatch(setPALValue(userProfile.PALValue));
  dispatch(setHasScale(null));
  let step = WeightTargetStep.BodyWeightScaleQuestion;
  const state = getState();
  const target = weightTargetInfoFromGoalSelector(state);
  if (target && !target.reached) {
    step = WeightTargetStep.BodyWeightReview;
  }
  dispatch(closeAllModals());
  if ((!target || target.reached) && hasScale) {
    dispatch(initialiseBodyScan(userProfile, false, true));
    return;
  }

  dispatch(toggleWeightTargetModal(true));
  dispatch(setWeightTargetStep(step));
};

export const userAnsweredHasScaleQuestion = (hasScale: boolean, userProfile: UserProfile) => (dispatch, getState) => {
  const { weightTarget: { data } } = getState();
  dispatch(setHasScale(hasScale));
  let isScanContinuingFromWeightTarget;
  let step;
  if (!hasScale) {
    isScanContinuingFromWeightTarget = false;
    dispatch(setIsScanContinuingFromWeightTarget(isScanContinuingFromWeightTarget));
    if (userProfile.longTermGoal === ProgramType.GainMuscleAndStrengthGoal) {
      step = WeightTargetStep.BodyWeightSupport;
    } else {
      step = WeightTargetStep.BodyWeightReview;
    }
    dispatch(setWeightTargetStep(step));
  } else {
    if (Platform.OS === 'web') {
      dispatch(toggleWeightTargetModal(false));
      dispatch(showContinueOnAppScreen(true));
      return; 
    }
    const hasTarget = data.weightTargetInfo?.target && !data.weightTargetInfo.target.reached;
    isScanContinuingFromWeightTarget = !hasTarget;
    dispatch(initialiseBodyScan(userProfile, false, isScanContinuingFromWeightTarget));
  }
};

export const backButonPressed = () => (dispatch, getState: () => IAppStore) => {
  const state = getState();
  const targetSummaryDashboard = weightTargetInfoFromGoalSelector(state);
  const targetInfo = getTargetInfo(state);
  const isScanContinuingFromWeightTarget = selectIsScanContinuingFromWeightTarget(state);
  const step = weightTargetStepSelector(state);
  const isChangingTarget = isChangingTargetSelector(state);
  if (step === WeightTargetStep.BodyWeightScaleQuestion) {
    dispatch(toggleWeightTargetModal(false));
    return;
  }
  if (
    step === WeightTargetStep.BodyWeightPal
    || step === WeightTargetStep.BodyWeightSupport
    || step === WeightTargetStep.BodyWeightReview
  ) {
    if (targetSummaryDashboard) {
      dispatch(toggleWeightTargetModal(false));
    } else {
      dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightScaleQuestion));
    }
    return;
  }
  if (
    (step === WeightTargetStep.BodyWeightSummary)
    && !isScanContinuingFromWeightTarget) {
    dispatch(toggleWeightTargetModal(false));
    return;
  }
  if (
    (step === WeightTargetStep.BodyWeightAdjust)
    && !isScanContinuingFromWeightTarget && targetSummaryDashboard) {
    if (isChangingTarget) {
      dispatch(closeTargetAdjustScreen());
      return;
    }
    dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightSummary));
    return;
  }
  if (
    step === WeightTargetStep.BodyWeightAdjust && isScanContinuingFromWeightTarget) {
    dispatch(toggleBodyScanModal(true));
    return;
  }
  if (step === WeightTargetStep.BodyWeightAdjust && targetInfo && targetInfo.reached) {
    dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightSummary));
    return;
  }
  if (
    step === WeightTargetStep.BodyWeightSummary && isScanContinuingFromWeightTarget) {
    dispatch(setScanProgress(ScanProgress.BeforeScan));
    dispatch(toggleBodyScanModal(true));
    return;
  }
  dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightReview));
};

export const updateReviewWeightAndGetTargetInfo = (
  userService,
  userProfileService,
  userProfile,
  reviewedWeight,
  PALValue,
) => async (dispatch) => {
  const reviewedWeightParsed = parseFloat(reviewedWeight);
  await saveReviewedWeightAndConfigureTargetInfo(
    dispatch,
    reviewedWeightParsed,
    userService,
    PALValue,
    userProfileService,
    userProfile,
  );
};

export const saveTarget = (userService, userProfile) => async (dispatch: any, getState) => {
  const state = getState();
  const { weightTarget: { data } } = state;

  const requestTime = new Date().valueOf();
  const timezone = moment.tz.guess();
  const userInfo: UserMetricInfo = {
    useImperialUnit: userProfile.useImperialUnit,
    height: parseFloat(userProfile.height),
    weight: parseFloat(userProfile.weight),
    longTermGoal: userProfile.longTermGoal,
  };

  const userTargetType = data.targetAdjustment.target.targetType;
  const { targetAdjustment } = data;

  let targetInfo: ITargetInfo | undefined;

  if (userTargetType === UserTargetTypes.Bodyweight) {
    targetInfo = {
      weight: userProfile.useImperialUnit
        ? MetricToImperialConverter.convertWeightToMetric(targetAdjustment.target.weight)
        : targetAdjustment.target.weight,
      date: targetAdjustment.target.date,
      shortDate: targetAdjustment.target.shortDate,
      monthsNeeded: targetAdjustment.target.monthsNeeded,
      duration: targetAdjustment.target.duration,
      weightChange: userProfile.useImperialUnit
        ? MetricToImperialConverter.convertWeightToMetric(targetAdjustment.target.weightChange)
        : targetAdjustment.target.weightChange,
      dailyRateOfChange: targetAdjustment.target.dailyRateOfChange,
      targetType: UserTargetTypes.Bodyweight,
    };
  } else if (userTargetType === UserTargetTypes.Musclemass) {
    targetInfo = {
      muscleMass: userProfile.useImperialUnit
        ? MetricToImperialConverter.convertWeightToMetric(targetAdjustment.target.muscleMass)
        : targetAdjustment.target.muscleMass,
      date: targetAdjustment.target.date,
      shortDate: targetAdjustment.target.shortDate,
      monthsNeeded: targetAdjustment.target.monthsNeeded,
      duration: targetAdjustment.target.duration,
      muscleMassChange: userProfile.useImperialUnit
        ? MetricToImperialConverter.convertWeightToMetric(targetAdjustment.target.muscleMassChange)
        : targetAdjustment.target.muscleMassChange,
      dailyRateOfChange: targetAdjustment.target.dailyRateOfChange,
      targetType: UserTargetTypes.Musclemass,
    };
  } else if (userTargetType === UserTargetTypes.Bodyfat) {
    targetInfo = {
      bodyFat: targetAdjustment.target.bodyFat,
      date: targetAdjustment.target.date,
      shortDate: targetAdjustment.target.shortDate,
      monthsNeeded: targetAdjustment.target.monthsNeeded,
      duration: targetAdjustment.target.duration,
      bodyFatChange: targetAdjustment.target.bodyFatChange,
      dailyRateOfChange: targetAdjustment.target.dailyRateOfChange,
      targetType: UserTargetTypes.Bodyfat,
    };
  }

  try {
    dispatch(requestWeightAndTargetSave());
    const result = await userService.addTargetForUser(
      userInfo,
      targetInfo,
      null,
      requestTime,
      timezone,
      userTargetType ? [UserTargetTypes[userTargetType]] : undefined,
    );
    dispatch(requestWeightTargetInfoSuccess(result, userTargetType));
    dispatch(requestBodyMetricStatsSuccess(result));
    dispatch(requestWeightAndTargetSaveSuccess());
    dispatch(setIsScanContinuingFromWeightTarget(false));
  } catch (error: any) {
    dispatch(requestWeightAndTargetSaveFailure(error.message));
  }
};

export const recalculateTargetValues = (userService, userProfile, target, isCurrentMetric) =>
  async (dispatch, getState) => {
    const state = getState();
    const { weightTarget: { data } } = state;
    const bodyscale = state.bodyscale || { data: {} };
    const scaleScanStats = bodyscale.data?.scaleScanStats;

    let targetMetricValues: ITargetMetricValues | undefined;

    if (scaleScanStats) {
      targetMetricValues = {
        muscleMass: scaleScanStats.calculatedMuscleMassMeterKg,
        bodyFat: scaleScanStats.calculatedBodyFat,
        bodyWeight: scaleScanStats.weight,
      };
    }

    const userTargetType = UserTargetTypes[data.targetAdjustment.target.targetType];
    const isCalculating = true;
    dispatch(setTargetWeight(target, isCalculating));
    const calculatedWeightTargetInfo = await calculateWeightTarget(
      userService,
      userProfile,
      data.reviewedWeight,
      target,
      userTargetType,
      targetMetricValues,
      isCurrentMetric,
    );
    dispatch(requestWeightTargetCalcSuccess(calculatedWeightTargetInfo, userTargetType));
  };

export const changeTarget = (userService: UserServiceClient) => async (dispatch) => {
  try {
    dispatch(loadChangeTargetScreenRequest());
    const timezone = moment.tz.guess();
    const requestTime = Date.now();
    const { minMaxDefault, weightTargetInfo, scaleScanStats } = await userService
      .getTargetChangeData(requestTime, timezone);
    dispatch(toggleWeightTargetModal(true));
    dispatch(loadChangeTargetScreenSuccess(weightTargetInfo, minMaxDefault, scaleScanStats));
  } catch (error: any) {
    dispatch(loadChangeTargetScreenFailure(error.message));
  }
};

export const removeTarget = () => async (dispatch) => {
  dispatch(toggleRemoveTargetModal(true));
};

export const confirmRemoveTarget = (userServiceClient: UserServiceClient) => async (dispatch) => {
  dispatch(removeTargetRequest());
  const requestTime = Date.now();
  const timezone = moment.tz.guess();
  try {
    await userServiceClient.resetTargetForUser();
    const targetAndBodyMetricSet = await userServiceClient.getTargetAndBodyMetricSetForUser(requestTime, timezone);
    dispatch(toggleRemoveTargetModal(false));
    dispatch(removeTargetSuccess(targetAndBodyMetricSet.bodyMetricSet, targetAndBodyMetricSet.target));
  } catch (error) {
    dispatch(removeTargetFailure());
  }
};

export const closeRemoveTargetModal = () => async (dispatch) => {
  dispatch(toggleRemoveTargetModal(false));
};

export const getCurrentTargetInfo = (userService) => async (dispatch, getState) => {
  const { weightTarget: { data } } = getState();
  dispatch(loadCurrentTargetInfo());
  const timezone = moment.tz.guess();
  const requestTime = new Date().valueOf();
  const { targetType } = data.weightTargetInfo.target;
  const userTargetType = targetType ? UserTargetTypes[targetType] : undefined;
  const calculatedMinMaxDefaultTargetInfo = await userService.calculateMinMaxDefaultTarget(
    requestTime,
    timezone,
    userTargetType,
  );
  dispatch(loadedCurrentTargetInfo(calculatedMinMaxDefaultTargetInfo, targetType));
};

const calculateWeightTarget = async (
  userService,
  userProfile,
  startWeight,
  targetWeight,
  userTargetType: UserTargetTypes,
  targetMetricValues: ITargetMetricValues | undefined,
  isCurrentMetric: boolean,
) => {
  const requestTime = new Date().valueOf();
  const timezone = moment.tz.guess();
  const userInfo = {
    useImperialUnit: userProfile.useImperialUnit,
    height: parseFloat(userProfile.height),
    weight: startWeight,
    longTermGoal: userProfile.longTermGoal,
    age: userProfile.age,
    skillLevel: userProfile.skillLevel,
    gender: userProfile.gender,
    PALValue: userProfile.PALValue,
    bodyComposition: userProfile.bodyComposition,
  };
  const targetType = userTargetType ? UserTargetTypes[userTargetType] : userTargetType;

  return await userService.calculateWeightTarget(
    userInfo,
    targetWeight,
    requestTime,
    timezone,
    targetType,
    targetMetricValues,
    isCurrentMetric,
  );
};

export const continueToSetYourWeightTargetButtonPressed = (
  userService,
  userProfile,
  PALValue,
  userProfileService,
) => async (dispatch, getState) => {
  const { bodyscale } = getState();
  const { rawScan, scaleScanStats } = bodyscale.data;
  dispatch(continueToSetTargetRequest());

  const targetMetricValues: ITargetMetricValues = {
    muscleMass: scaleScanStats.calculatedMuscleMassMeterKg,
    bodyFat: scaleScanStats.calculatedBodyFat,
    bodyWeight: rawScan.weight,
  };

  await saveReviewedWeightAndConfigureTargetInfo(
    dispatch,
    rawScan.weight,
    userService,
    PALValue,
    userProfileService,
    userProfile,
    targetMetricValues,
    rawScan,
  );
  dispatch(toggleWeightTargetModal(true));
  dispatch(setWeightTargetStep(WeightTargetStep.BodyWeightAdjust));
  dispatch(continueToSetTargetSuccess());
};

const loadDataForTargetWeightScreen = async (
  dispatch: any,
  userProfile: UserProfile,
  reviewedWeightParsed: number,
  PALValue: number,
  userService: UserServiceClient,
  userTargetType: UserTargetTypes,
  targetMetricValues?: ITargetMetricValues,
) => {
  const requestTime = new Date().valueOf();
  const timezone = moment.tz.guess();
  const userInfo: UserMetricInfo = {
    useImperialUnit: userProfile.useImperialUnit,
    height: userProfile.height,
    weight: reviewedWeightParsed,
    longTermGoal: userProfile.longTermGoal,
    age: userProfile.age,
    skillLevel: userProfile.skillLevel,
    gender: userProfile.gender,
    PALValue,
    bodyComposition: userProfile.bodyComposition,
  };
  dispatch(requestWeightTargetCalc());
  const calculatedMinMaxDefaultTargetInfo = await userService
    .calculateMinMaxDefaultTarget(requestTime, timezone, userTargetType);
  const calculatedWeightTargetInfo = await userService.calculateWeightTarget(
    userInfo,
    calculatedMinMaxDefaultTargetInfo.default,
    requestTime,
    timezone,
    userTargetType,
    targetMetricValues,
  );
  dispatch(requestWeightTargetCalcSuccess(
    calculatedWeightTargetInfo,
    userTargetType,
    calculatedMinMaxDefaultTargetInfo,
  ));
};

export async function saveReviewedWeightAndConfigureTargetInfo(
  dispatch: any,
  reviewedWeightParsed: number,
  userService: any,
  PALValue: any,
  userProfileService: any,
  userProfile: any,
  targetMetricValues?: ITargetMetricValues,
  rawScan?: any,
) {
  try {
    const userTargetType = calculateUserTargetType(userProfile, !!rawScan);
    const requestTime = new Date().valueOf();
    const timezone = moment.tz.guess();
    dispatch(requestWeightTargetInfo(reviewedWeightParsed));

    const targetInfo = await userService
      .reviewWeightAndGetTargetInfo(
        reviewedWeightParsed,
        PALValue,
        requestTime,
        timezone,
        userTargetType ? UserTargetTypes[userTargetType] : undefined,
        rawScan,
      );

    await userProfileService.refreshCurrentUserProfile();
    if (targetInfo.weightTargetInfo.target && !targetInfo.weightTargetInfo.target.reached) {
      dispatch(requestBodyMetricStatsSuccess(targetInfo));
    } else {
      await loadDataForTargetWeightScreen(
        dispatch,
        userProfile,
        reviewedWeightParsed,
        PALValue,
        userService,
        userTargetType,
        targetMetricValues,
      );
    }

    dispatch(requestWeightTargetInfoSuccess(targetInfo, userTargetType));

    if (targetInfo.weightTargetInfo.target && targetInfo.weightTargetInfo.target.reached) {
      dispatch(resetTargetRequest());
      const result = await userService.getTargetAndBodyMetricSetForUser(requestTime, timezone);
      dispatch(requestBodyMetricStatsSuccess(result));
      dispatch(resetTargetRequestSuccess(result));
    }
  } catch {
    // TODO: Temporary till rework can be done on actions
    dispatch(continueToSetTargetFailure());
  }
}

export function calculateUserTargetType(userProfile: UserProfile, hasScale = true) {
  const longTermGoal = ProgramType[userProfile.longTermGoal];
  let userTargetType;
  if (longTermGoal === ProgramType.GetLeanAndFitGoal
    || longTermGoal === ProgramType.GetStrongAndFitGoal
    || longTermGoal === ProgramType.MobilityAndTone
    || longTermGoal === ProgramType.MuscleAndShred) {
    userTargetType = UserTargetTypes.Bodyweight;
  } else if (longTermGoal === ProgramType.GainMuscleAndStrengthGoal) {
    userTargetType = UserTargetTypes.Musclemass;
  }

  if ((
    longTermGoal === ProgramType.GetStrongAndFitGoal
    || longTermGoal === ProgramType.MobilityAndTone
    || longTermGoal === ProgramType.MuscleAndShred) && hasScale) {
    userTargetType = UserTargetTypes.Bodyfat;
  }

  return userTargetType;
}
